<script setup>
import { ref } from 'vue'

import { useNotify } from '@/composables/notify'
import { DxForm } from 'devextreme-vue/form'

import * as dataGrid from '@/composables/data-grid'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

const props = defineProps({
  translations: {
    type: Object,
    default: null,
  },
})

const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()

const { notifySuccess, notifyError } = useNotify()

const infoFormRef = ref(null)
const isSubmitting = ref(false)

const infoForm = ref({ contactName: null, phone: null, email: null, notes: null })
const infoFormItems = [
  {
    itemType: 'group',
    caption: props.translations['common.requestInfo'],
    colSpan: 2,
    items: [
      {
        dataField: 'contactName',
        label: { text: props.translations['common.contactName'] },
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'phone',
        label: { text: props.translations['common.phone'] },
        editorOptions: { tabIndex: 0, onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }},
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'email',
        label: { text: props.translations['common.email'] }
      },
      {
        dataField: 'notes',
        editorType: 'dxTextArea',
        label: { text: props.translations['common.note'] },
        colSpan: 2,
        editorOptions: { height: '5rem' }
      },
    ],
  },
]

const onSendClick = async() => {
  const validationResult = infoFormRef.value.instance.validate()
  if (!validationResult.isValid) { return }

  isSubmitting.value = true
  const subject = props.translations['common.catalogInfoRequest']
  const message = `
    <p><strong>${props.translations['common.customerCode']}:</strong> ${webSiteStore.user.tenant_code}<br>
    <strong>${props.translations['common.nameAlt']}:</strong> ${registryStore.companyData.name}</p>

    <p><strong>${props.translations['common.contactName']}:</strong> ${infoForm.value.contactName}<br>
    <strong>${props.translations['common.phone']}:</strong> ${infoForm.value.phone}<br>
    ${infoForm.value.email ? `<strong>${props.translations['common.email']}:</strong> ${infoForm.value.email}<br>` : ``}
    </p>

    ${infoForm.value.notes ? `
    <p><strong>${props.translations['common.note']}:</strong><br>
    ${infoForm.value.notes}</p>
    ` : ``}
  `
  try {
    await webSiteStore.api.sendEmailAsync({ subject, message })
    notifySuccess(props.translations['common.emailSent'])
    setTimeout(() => {
      window.close()
    }, 1500)
  } catch (error) {
    notifyError(props.translations['error.sendingRequest'])
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
    <div class="w-full max-w-lg">
      <div class="mb-8 text-center">
        <h2 class="text-2xl font-semibold text-slate-900 mb-2">
          {{ props.translations['common.catalogInfoRequest'] }}
        </h2>
        <p class="text-slate-600 leading-relaxed">
          {{ props.translations['common.accessRestrictedCatalog'] }}
        </p>
      </div>

      <div class="bg-white rounded-2xl shadow-lg shadow-slate-200 overflow-hidden border border-slate-100">
        <div class="p-8">
          <dx-form
            ref="infoFormRef"
            :form-data="infoForm"
            :items="infoFormItems"
          />
        </div>

        <div class="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-slate-200" />

        <div class="p-8 bg-slate-50 flex gap-3 justify-end">
          <q-btn
            class="q-ml-md"
            icon="mdi-send"
            :label="props.translations['common.submit']"
            color="primary"
            @click="onSendClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>
